<!-- src/components/dashboard/RevenueComparisonChartCard.vue -->
<template>
  <div class="card revenue-comparison-card">
    <div class="card-content" style="padding-bottom: 10px">
      <div class="row" style="margin-bottom: 6px">
        <div class="col s12">
          <div
            class="revenue-comparison-top"
            style="
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 12px;
              flex-wrap: wrap;
            "
          >
            <div>
              <div
                class="revenue-comparison-title"
                style="font-size: 20px; font-weight: 700"
              >
                {{ headerTitle }}
              </div>

              <div
                class="revenue-comparison-meta"
                style="
                  margin-top: 6px;
                  display: flex;
                  gap: 10px;
                  align-items: center;
                  flex-wrap: wrap;
                "
              >
                <span
                  class="chip"
                  style="height: 26px; line-height: 26px; font-size: 12px"
                >
                  Mon–Sun
                </span>

                <div class="grey-text text-darken-1" style="font-size: 13px">
                  Week: {{ weekLabel || "—" }}
                </div>
              </div>
            </div>

            <div class="hide-on-small-only" style="min-width: 420px">
              <ul
                ref="tabsEl"
                class="tabs tabs-fixed-width"
                style="background: transparent"
              >
                <li class="tab">
                  <a :href="`#${tabIdAll}`" class="active"> All </a>
                </li>

                <li class="tab">
                  <a :href="`#${tabIdRF}`"> Rev vs Fcst </a>
                </li>

                <li class="tab">
                  <a :href="`#${tabIdRB}`"> Rev vs Bud </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="hide-on-med-and-up" style="margin-top: 10px">
            <ul
              ref="tabsElMobile"
              class="tabs tabs-fixed-width"
              style="background: transparent"
            >
              <li class="tab">
                <a :href="`#${tabIdAll}`" class="active"> All </a>
              </li>

              <li class="tab">
                <a :href="`#${tabIdRF}`"> Rev/Fcst </a>
              </li>

              <li class="tab">
                <a :href="`#${tabIdRB}`"> Rev/Bud </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="kpi-grid" :class="kpiGridClass">
        <div class="kpi-box">
          <div class="kpi-label">ACTUAL</div>

          <div class="kpi-value">
            {{ moneyGBP(actualTotal) }}
          </div>

          <div class="kpi-sub">Tills total</div>
        </div>

        <div v-if="mode !== 'rb'" class="kpi-box">
          <div class="kpi-label">FORECAST</div>

          <div class="kpi-value">
            {{ moneyGBP(forecastTotal) }}
          </div>

          <div class="kpi-sub">Forecast total</div>
        </div>

        <div v-if="mode !== 'rb'" class="kpi-box">
          <div class="kpi-label">VARIANCE</div>

          <div class="kpi-value" :style="{ color: varianceAFColour }">
            {{ signedMoneyGBP(varianceAF) }}
          </div>

          <div class="kpi-sub">Actual – Forecast</div>
        </div>

        <div v-if="mode !== 'rf'" class="kpi-box">
          <div class="kpi-label">BUDGET</div>

          <div class="kpi-value">
            {{ moneyGBP(budgetTotal) }}
          </div>

          <div class="kpi-sub">Budget total</div>
        </div>

        <div v-if="mode !== 'rf'" class="kpi-box">
          <div class="kpi-label">VARIANCE</div>

          <div class="kpi-value" :style="{ color: varianceABColour }">
            {{ signedMoneyGBP(varianceAB) }}
          </div>

          <div class="kpi-sub">Actual – Budget</div>
        </div>
      </div>

      <div class="divider" style="margin: 10px 0 0"></div>
    </div>

    <div class="card-content" style="padding-top: 14px">
      <div :id="tabIdAll"></div>
      <div :id="tabIdRF"></div>
      <div :id="tabIdRB"></div>

      <div v-if="loading" class="progress" style="margin: 0 0 10px 0">
        <div class="indeterminate"></div>
      </div>

      <div class="revenue-comparison-chart-wrap">
        <canvas ref="chartCanvas"></canvas>
      </div>

      <div class="grey-text text-darken-1" style="font-size: 12px; margin-top: 10px">
        Hover over a day to compare values.
      </div>

      <div
        v-if="budgetUsesWeeklyOnly"
        class="amber-text text-darken-3"
        style="font-size: 12px; margin-top: 6px"
      >
        Budget is stored as weekly line totals, so chart budget is spread evenly across
        Mon–Sun.
      </div>

      <div
        v-if="error"
        class="red-text text-darken-2"
        style="font-size: 12px; margin-top: 6px"
      >
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";

import M from "materialize-css";

import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";

import { db } from "@/firebase";

import { doc, getDoc, getDocs, collection } from "firebase/firestore";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { fetchForecastWeekDailyTotals } from "@/scripts/useScripts/useForecasts";

import { moneyGBP, formatWeekRange } from "@/scripts/sharedHelpers/formatter";

import { venueAuditColour } from "@/scripts/sharedHelpers/auditColours";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
);

const { venueId, tradingDate, weekEnding } = useVenueContext();

const mode = ref("all");

const tabsEl = ref(null);
const tabsElMobile = ref(null);
const chartCanvas = ref(null);

let tabsInstance = null;
let tabsInstanceMobile = null;
let chartInstance = null;

const loading = ref(false);
const error = ref("");

const uid = Math.random().toString(36).slice(2, 9);

const tabIdAll = `revcmp-all-${uid}`;

const tabIdRF = `revcmp-rf-${uid}`;

const tabIdRB = `revcmp-rb-${uid}`;

const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function toNumber(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function signedMoneyGBP(n) {
  const v = toNumber(n);

  const sign = v > 0 ? "+" : "";

  return `${sign}${moneyGBP(v)}`;
}

function varianceColour(v) {
  const n = toNumber(v);

  if (n > 0) {
    return venueAuditColour(100);
  }

  if (n < 0) {
    return venueAuditColour(0);
  }

  return "#616161";
}

function parseISODate(iso) {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map(Number);

  if (!y || !m || !d) {
    return null;
  }

  return new Date(y, m - 1, d);
}

function toISODateLocal(d) {
  const y = d.getFullYear();

  const m = String(d.getMonth() + 1).padStart(2, "0");

  const day = String(d.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
}

const weekTo = computed(() => {
  const we = parseISODate(String(weekEnding.value || "").slice(0, 10));

  if (we) {
    return toISODateLocal(we);
  }

  const td = parseISODate(String(tradingDate.value || "").slice(0, 10));

  return td ? toISODateLocal(td) : "";
});

const weekFrom = computed(() => {
  const end = parseISODate(weekTo.value);

  if (!end) {
    return "";
  }

  const start = new Date(end);

  start.setDate(start.getDate() - 6);

  return toISODateLocal(start);
});

const weekLabel = computed(() => {
  const s = parseISODate(weekFrom.value);

  const e = parseISODate(weekTo.value);

  if (!s || !e) {
    return "";
  }

  return formatWeekRange(s, e);
});

const actualDailyTotals = ref([0, 0, 0, 0, 0, 0, 0]);

async function loadActuals() {
  const start = parseISODate(weekFrom.value);

  if (!venueId.value || !start) {
    actualDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    return;
  }

  const arr = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date(start);

    d.setDate(d.getDate() + i);

    const iso = toISODateLocal(d);

    try {
      const tillsSnap = await getDocs(
        collection(db, "venues", venueId.value, "tills_days", iso, "tills")
      );

      const dayTotal = tillsSnap.docs.reduce((sum, snap) => {
        const data = snap.data() || {};

        return (
          sum +
          toNumber(
            data.counted_total ??
              data.countedTotal ??
              data.actual_total ??
              data.actualTotal ??
              data.total ??
              data.net_sales ??
              data.netSales ??
              0
          )
        );
      }, 0);

      arr.push(dayTotal);
    } catch (e) {
      console.warn(
        "[RevenueComparisonChartCard] tills subcollection read failed:",
        iso,
        e
      );

      arr.push(0);
    }
  }

  actualDailyTotals.value = arr;
}

const actualTotal = computed(() =>
  actualDailyTotals.value.reduce((sum, v) => sum + toNumber(v), 0)
);

const forecastDailyTotals = ref([0, 0, 0, 0, 0, 0, 0]);

async function loadForecast() {
  if (!venueId.value || !weekEnding.value) {
    forecastDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    return;
  }

  const weISO = String(weekEnding.value || "").slice(0, 10);

  try {
    const arr = await fetchForecastWeekDailyTotals({
      venue_id: venueId.value,
      week_ending_date: weISO,
    });

    forecastDailyTotals.value =
      Array.isArray(arr) && arr.length === 7 ? arr.map(toNumber) : [0, 0, 0, 0, 0, 0, 0];
  } catch (e) {
    console.warn("[RevenueComparisonChartCard] forecast load failed:", e);

    forecastDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];
  }
}

const forecastTotal = computed(() =>
  forecastDailyTotals.value.reduce((sum, v) => sum + toNumber(v), 0)
);

const budgetDailyTotals = ref([0, 0, 0, 0, 0, 0, 0]);

const budgetUsesWeeklyOnly = ref(false);

function normalizeBudgetArray(maybeArr) {
  if (Array.isArray(maybeArr) && maybeArr.length === 7) {
    return maybeArr.map(toNumber);
  }

  return null;
}

function mapDaysObjectToArray(daysObj) {
  if (!daysObj || typeof daysObj !== "object") {
    return null;
  }

  return WEEKDAY_KEYS.map((k) => toNumber(daysObj[k] ?? 0));
}

function spreadWeeklyTotalEvenly(total) {
  const weekly = toNumber(total);

  const perDay = weekly / 7;

  return Array.from({ length: 7 }, () => perDay);
}

function extractDailyBudgetFromLineDoc(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  const direct =
    normalizeBudgetArray(data.daily_totals) ||
    normalizeBudgetArray(data.dailyTotals) ||
    normalizeBudgetArray(data.days);

  if (direct) {
    return direct;
  }

  const daysObj = mapDaysObjectToArray(data.days);

  if (daysObj && daysObj.some((v) => v !== 0)) {
    return daysObj;
  }

  const monSun = WEEKDAY_KEYS.map((k) => toNumber(data[k] ?? 0));

  if (monSun.some((v) => v !== 0)) {
    return monSun;
  }

  return null;
}

async function loadBudget() {
  if (!venueId.value || !weekEnding.value) {
    budgetDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    budgetUsesWeeklyOnly.value = false;

    return;
  }

  const weISO = String(weekEnding.value || "").slice(0, 10);

  try {
    const weekRef = doc(db, "venues", venueId.value, "budget_weeks", weISO);

    const weekSnap = await getDoc(weekRef);

    if (!weekSnap.exists()) {
      budgetDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

      budgetUsesWeeklyOnly.value = false;

      return;
    }

    const weekData = weekSnap.data() || {};

    const directWeek =
      normalizeBudgetArray(weekData.daily_totals) ||
      normalizeBudgetArray(weekData.dailyTotals) ||
      normalizeBudgetArray(weekData.days);

    if (directWeek) {
      budgetDailyTotals.value = directWeek;

      budgetUsesWeeklyOnly.value = false;

      return;
    }

    const weekDaysObj = mapDaysObjectToArray(weekData.days);

    if (weekDaysObj && weekDaysObj.some((v) => v !== 0)) {
      budgetDailyTotals.value = weekDaysObj;

      budgetUsesWeeklyOnly.value = false;

      return;
    }

    const managerContract = toNumber(weekData.managers_contract);

    const linesSnap = await getDocs(
      collection(db, "venues", venueId.value, "budget_weeks", weISO, "lines")
    );

    if (linesSnap.empty) {
      budgetDailyTotals.value = spreadWeeklyTotalEvenly(managerContract);

      budgetUsesWeeklyOnly.value = managerContract !== 0;

      return;
    }

    let mergedDaily = [0, 0, 0, 0, 0, 0, 0];

    let foundDailyShape = false;

    let weeklyCategoryTotal = 0;

    linesSnap.forEach((lineDoc) => {
      const data = lineDoc.data() || {};

      const daily = extractDailyBudgetFromLineDoc(data);

      if (daily) {
        foundDailyShape = true;

        mergedDaily = mergedDaily.map((v, i) => v + toNumber(daily[i]));
      }

      weeklyCategoryTotal += toNumber(data.amount ?? 0);
    });

    weeklyCategoryTotal += managerContract;

    if (foundDailyShape) {
      budgetDailyTotals.value = mergedDaily;

      budgetUsesWeeklyOnly.value = false;

      return;
    }

    budgetDailyTotals.value = spreadWeeklyTotalEvenly(weeklyCategoryTotal);

    budgetUsesWeeklyOnly.value = true;
  } catch (e) {
    console.warn("[RevenueComparisonChartCard] budget_weeks read failed:", e);

    budgetDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    budgetUsesWeeklyOnly.value = false;
  }
}

const budgetTotal = computed(() =>
  budgetDailyTotals.value.reduce((sum, v) => sum + toNumber(v), 0)
);

const varianceAF = computed(() => actualTotal.value - forecastTotal.value);

const varianceAB = computed(() => actualTotal.value - budgetTotal.value);

const varianceAFColour = computed(() => varianceColour(varianceAF.value));

const varianceABColour = computed(() => varianceColour(varianceAB.value));

const headerTitle = computed(() => {
  if (mode.value === "all") {
    return "Revenue vs Forecast vs Budget";
  }

  if (mode.value === "rb") {
    return "Revenue vs Budget";
  }

  return "Revenue vs Forecast";
});

const kpiGridClass = computed(() => {
  if (mode.value === "all") {
    return "kpi-grid-5";
  }

  return "kpi-grid-3";
});

function chartOptions() {
  return {
    responsive: true,

    maintainAspectRatio: false,

    interaction: {
      mode: "index",
      intersect: false,
    },

    plugins: {
      legend: {
        display: true,
      },

      tooltip: {
        callbacks: {
          label(ctx) {
            const v = ctx.parsed?.y ?? 0;

            return `${ctx.dataset.label}: ${moneyGBP(v)}`;
          },
        },
      },
    },

    scales: {
      y: {
        ticks: {
          callback(value) {
            return moneyGBP(value);
          },
        },
      },
    },

    elements: {
      line: {
        tension: 0.25,
        borderWidth: 2,
      },

      point: {
        radius: 3,
        hoverRadius: 5,
      },
    },
  };
}

function datasetsForMode(m) {
  const dsActual = {
    label: "Actual",
    data: actualDailyTotals.value,
    borderColor: "#2196f3",
    backgroundColor: "#2196f3",
    pointBorderColor: "#2196f3",
    pointBackgroundColor: "#ffffff",
    pointBorderWidth: 2,
    fill: false,
  };

  const dsForecast = {
    label: "Forecast",
    data: forecastDailyTotals.value,
    borderColor: "#ff5c7a",
    backgroundColor: "#ff5c7a",
    pointBorderColor: "#ff5c7a",
    pointBackgroundColor: "#ffffff",
    pointBorderWidth: 2,
    fill: false,
  };

  const dsBudget = {
    label: "Budget",
    data: budgetDailyTotals.value,
    borderColor: "#ff9800",
    backgroundColor: "#ff9800",
    pointBorderColor: "#ff9800",
    pointBackgroundColor: "#ffffff",
    pointBorderWidth: 2,
    fill: false,
  };

  if (m === "all") {
    return [dsActual, dsForecast, dsBudget];
  }

  if (m === "rb") {
    return [dsActual, dsBudget];
  }

  return [dsActual, dsForecast];
}

function renderOrUpdateChart() {
  if (!chartCanvas.value) {
    return;
  }

  const ctx = chartCanvas.value.getContext("2d");

  const data = {
    labels,

    datasets: datasetsForMode(mode.value),
  };

  if (chartInstance) {
    chartInstance.data.labels = data.labels;

    chartInstance.data.datasets = data.datasets;

    chartInstance.update();

    return;
  }

  chartInstance = new Chart(ctx, {
    type: "line",
    data,
    options: chartOptions(),
  });
}

function modeFromHref(href) {
  if (href === `#${tabIdAll}`) {
    return "all";
  }

  if (href === `#${tabIdRB}`) {
    return "rb";
  }

  return "rf";
}

function initTabsSafe(el, setter) {
  if (!el) {
    return null;
  }

  try {
    return M.Tabs.init(el, {
      swipeable: false,

      onShow: () => {
        const active = el.querySelector("a.active");

        const href = active?.getAttribute("href") || `#${tabIdAll}`;

        setter(modeFromHref(href));
      },
    });
  } catch (e) {
    console.warn("[RevenueComparisonChartCard] tabs init failed:", e);

    return null;
  }
}

async function loadAll() {
  error.value = "";

  if (!venueId.value) {
    actualDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    forecastDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    budgetDailyTotals.value = [0, 0, 0, 0, 0, 0, 0];

    return;
  }

  loading.value = true;

  try {
    await Promise.all([loadActuals(), loadForecast(), loadBudget()]);

    renderOrUpdateChart();
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  tabsInstance = initTabsSafe(tabsEl.value, (m) => {
    mode.value = m;
  });

  tabsInstanceMobile = initTabsSafe(tabsElMobile.value, (m) => {
    mode.value = m;
  });

  await loadAll();

  queueMicrotask(() => {
    renderOrUpdateChart();
  });
});

watch([venueId, weekEnding, tradingDate], loadAll);

watch(
  () => mode.value,
  () => renderOrUpdateChart()
);

watch(
  () => actualDailyTotals.value,
  () => renderOrUpdateChart(),
  {
    deep: true,
  }
);

watch(
  () => forecastDailyTotals.value,
  () => renderOrUpdateChart(),
  {
    deep: true,
  }
);

watch(
  () => budgetDailyTotals.value,
  () => renderOrUpdateChart(),
  {
    deep: true,
  }
);

onBeforeUnmount(() => {
  if (tabsInstance) {
    tabsInstance.destroy();
  }

  if (tabsInstanceMobile) {
    tabsInstanceMobile.destroy();
  }

  if (chartInstance) {
    chartInstance.destroy();
  }
});
</script>

<style scoped>
.tabs .tab a {
  color: rgba(0, 0, 0, 0.6);
  font-weight: 700;
  text-transform: none;
  font-size: 12px;
}

.tabs .tab a.active {
  color: rgba(0, 0, 0, 0.88);
}

.tabs .indicator {
  background-color: rgba(0, 0, 0, 0.25);
}

.kpi-box {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  min-height: 108px;

  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kpi-label {
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.kpi-value {
  margin-top: 6px;
  font-weight: 800;
  line-height: 1.05;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  font-size: 28px;
}

.kpi-sub {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  white-space: nowrap;
}

.kpi-grid {
  display: grid;
  gap: 14px;
  margin-bottom: 0;
}

.kpi-grid-5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.kpi-grid-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.revenue-comparison-card {
  overflow: hidden;
}

.revenue-comparison-chart-wrap {
  height: 340px;
}

@media (max-width: 1200px) {
  .kpi-value {
    font-size: 24px;
  }
}

@media (max-width: 992px) {
  .kpi-value {
    font-size: 22px;
  }

  .kpi-grid-5,
  .kpi-grid-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .revenue-comparison-top {
    display: block !important;
  }

  .revenue-comparison-title {
    font-size: 18px !important;
    line-height: 1.25;
  }

  .revenue-comparison-meta {
    gap: 6px !important;
  }

  .tabs .tab a {
    font-size: 11px;
    padding: 0 6px;
  }

  .kpi-grid-5,
  .kpi-grid-3 {
    grid-template-columns: 1fr;
  }

  .kpi-grid {
    gap: 10px;
  }

  .kpi-box {
    min-height: 0;
    padding: 12px;
  }

  .kpi-value {
    font-size: 20px;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .kpi-sub {
    white-space: normal;
  }

  .revenue-comparison-chart-wrap {
    height: 280px;
  }
}
</style>
